import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { LclIsfEsLinkModel, LCLShipmentModel, PackageModel } from '@data-layer/models/lcl';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { HeaderType } from '@app/modules/app-layout/enums/headerType';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { enumLclTrackingStates } from '@enum/enumLclTrackingStates';
import { EntityModel } from '@data-layer/models/entities';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { LclContainerService } from '@shared/lcl-mutual/services';
import { TranslateService } from '@ngx-translate/core';
import { DateTimezoneService } from '@services/date-timezone.service';
import { enumPackagesConfirmationState, enumShipmentState, enumVoyageStatus } from '@data-layer/enums/lcl';
import { PaginatedResultModel } from '@data-layer/models/common';
import { BillItemPendingModel, BillModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { takeUntil } from 'rxjs/operators';
import { GenericService } from '@services/generic.service';
import { UntypedFormGroup } from '@angular/forms';
import { IsfModel } from '@data-layer/models/isf';
import { enumIsfState } from '@app/data-layer/enums/isf';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { enumEntrySummaryState } from '@app/data-layer/enums/entry-summaries';
import { enumShippingRole, enumUserBetaFlag } from '@data-layer/enums/user';
import { LclShipmentPaymentCardComponent } from '@app/modules/lcl-shipping/components';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { DocumentModel } from '@data-layer/models/documents';
import { enumMarineInsurancePendingPaymentAction } from '@data-layer/enums/marine-insurance';
import { MarineInsuranceSideformService } from '@shared/marine-insurance-shared/services';
import { enumPaymentState } from '@app/data-layer/enums/payments';

@Component({
  selector: 'app-lcl-edit-shipment',
  templateUrl: './lcl-edit-shipment.component.html',
  styleUrls: ['./lcl-edit-shipment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclEditShipmentComponent implements OnInit, OnDestroy {
  @Input() public shipment: LCLShipmentModel;
  @Input() public payedBills: PaginatedResultModel<BillModel>;
  @Input() public pendingBills: PaymentBillItemPendingModel;
  @Input() public linkedISF: IsfModel;
  @Input() public linkedES: EntrySummaryModel;
  @Input() public isCouponAvailable: boolean;
  @Input() public isCompanyFromUSA: boolean = true;
  @Input() public isInsuranceAvailable: boolean;
  @Input() public isInsuranceLoading: boolean;
  @Input() public currentInsurance: MarineInsuranceModel;
  @Input() public currentInsuranceDocuments: DocumentModel;
  @Input() public pendingMarineInsuranceBills: PaymentBillItemPendingModel;
  @Output() public updateShipment: EventEmitter<LCLShipmentModel> = new EventEmitter<LCLShipmentModel>();
  @Output() public updateEntity: EventEmitter<EntityModel> = new EventEmitter<EntityModel>();
  @Output() public removeEntity: EventEmitter<number> = new EventEmitter<number>();
  @Output() public updatePackages: EventEmitter<{ shipmentId: number; packages: PackageModel[] }> = new EventEmitter<any>();
  @Output() public fileISF: EventEmitter<LclIsfEsLinkModel> = new EventEmitter<LclIsfEsLinkModel>();
  @Output() public fileES: EventEmitter<LclIsfEsLinkModel> = new EventEmitter<LclIsfEsLinkModel>();
  @Output() public applyCoupon: EventEmitter<string> = new EventEmitter<string>();
  @Output() public downloadAllFiles: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public openMarineInsuranceQuoteForm: EventEmitter<boolean> = new EventEmitter<boolean>();

  public isLaptop: boolean = false;
  public isShowProductBoxes: boolean = false;
  public headerTypes: typeof HeaderType = HeaderType;
  public TRACKING_STATES: typeof enumLclTrackingStates = enumLclTrackingStates;
  public enumIsfState: typeof enumIsfState = enumIsfState;
  public enumEntrySummaryState: typeof enumEntrySummaryState = enumEntrySummaryState;
  public headerType$: BehaviorSubject<number> = this.appLayoutService.headerTypeSource$;
  public company: CompanyModel;
  public form: UntypedFormGroup;

  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public enumPaymentState: typeof enumPaymentState = enumPaymentState;
  public paidOrSentPaymentStates: enumPaymentState[] = [
    enumPaymentState.SUCCESS,
    enumPaymentState.PARTIALLY_REFUNDED,
    enumPaymentState.DELAYED,
  ];

  public enumMarineInsurancePendingPaymentAction: typeof enumMarineInsurancePendingPaymentAction = enumMarineInsurancePendingPaymentAction;
  public enumShipmentState: typeof enumShipmentState = enumShipmentState;

  @ViewChild(LclShipmentPaymentCardComponent) private lclShipmentPaymentCardComponentRef: LclShipmentPaymentCardComponent;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private appLayoutService: AppLayoutService,
    private lclContainerService: LclContainerService,
    private companiesService: CompaniesService,
    private translateService: TranslateService,
    private dateTimezoneService: DateTimezoneService,
    private genericService: GenericService,
    private cdr: ChangeDetectorRef,
    private marineInsuranceSideformService: MarineInsuranceSideformService,
  ) {}

  public ngOnInit(): void {
    this.genericService.isLaptop$.pipe(takeUntil(this.destroy$)).subscribe((isLaptop: boolean) => {
      this.isLaptop = isLaptop;
    });

    this.appLayoutService.headerTypeSource$.next(HeaderType.LCLShipment);
    // TODO check header

    this.company = this.companiesService.getCompany();
    this.isShowProductBoxes =
      (this.company.isUnitedStates || (!this.company.isUnitedStates && this.company.isAllowedForCustoms)) &&
      this.shipment.voyage?.route?.arrivalPort?.country?.code === USA_COUNTRY_CODE;
  }

  public ngOnDestroy(): void {
    this.appLayoutService.headerTypeSource$.next(0);
    this.destroy$.next();
  }

  public onSavePackages(packages: PackageModel[]): void {
    packages = this.lclContainerService.convertPackagesDataForUpdateSending(packages || []);

    this.updatePackages.emit({ shipmentId: this.shipment.id, packages: packages });
  }

  public onRemovePackage(id: number): void {
    this.updatePackages.emit({
      shipmentId: this.shipment.id,
      packages: this.shipment.packages.filter((shipmentPackage: PackageModel) => shipmentPackage.id !== id),
    });
  }

  public setCouponError(errorMessage: string): void {
    this.lclShipmentPaymentCardComponentRef.setCouponError(errorMessage);
    this.cdr.markForCheck();
  }

  public get isEditablePackages(): boolean {
    return (
      this.shipment.trackingState < enumLclTrackingStates.AT_ORIGIN_WAREHOUSE &&
      this.shipment.packagesConfirmationState < enumPackagesConfirmationState.SUPPORT_DRAFT &&
      this.shipment.voyage.status <= enumVoyageStatus.LOADED &&
      this.shipment.state !== enumShipmentState.CANCELED
    );
  }

  public get isEditableDocs(): boolean {
    return (
      this.shipment.trackingState < enumLclTrackingStates.AT_ORIGIN_WAREHOUSE && this.shipment.voyage.status <= enumVoyageStatus.LOADED
    );
  }

  public get isEditableContacts(): boolean {
    return this.shipment.state !== enumShipmentState.CANCELED && this.shipment.trackingState === enumLclTrackingStates.BOOKING_PLACED;
  }

  public get stepDescription(): string {
    if (!this.shipment) {
      return;
    }

    let roleStr: string = this.shipment.shippingRole === enumShippingRole.SHIPPER ? 'SHIPPER' : 'BUYER';

    // #8055
    if ((!this.isCompanyFromUSA && this.shipment?.shippingRole === enumShippingRole.IMPORTER) || !this.isDestinationUSA) {
      roleStr = 'IMPORTER_FOREIGN';
    }

    return this.translateService.instant(
      `LCL_SHIPMENT.SHIPMENT_STEPS_DESCRIPTION.${roleStr}.${enumLclTrackingStates[this.shipment?.trackingState]}`,
      {
        cutoffDate: this.dateTimezoneService.format(this.shipment.voyage?.warehouseCutoff, 'MM/DD/YYYY'),
      },
    );
  }

  public get isHasInstantPayment(): boolean {
    return (this.pendingBills?.items || []).some((bill: BillItemPendingModel) => bill.paymentType === enumCompanyPaymentType.INSTANT);
  }

  public get isPayment(): boolean {
    return (
      ((this.shipment?.trackingState > this.TRACKING_STATES.AT_ORIGIN_WAREHOUSE && this.pendingBills?.items?.length > 0) ||
        (this.shipment.isOpenForPayment && this.pendingBills?.items?.length > 0)) &&
      this.isHasInstantPayment
    );
  }

  public get isDestinationUSA(): boolean {
    return this.shipment.voyage?.route?.arrivalPort?.country?.code === USA_COUNTRY_CODE;
  }

  public get isPaymentPending(): boolean {
    return this.shipment.paymentState === enumPaymentState.PENDING;
  }
}
